import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import Located from "@mapmycustomers/shared/types/base/Located";
import { Lead } from "@mapmycustomers/shared/types/entity";
import { Cluster, EntityPin, MultiPin } from "@mapmycustomers/shared/types/map";
import PersistentMapSettings from "@mapmycustomers/shared/types/persistent/PersistentMapSettings";

import MapMode from "@app/enum/map/MapMode";
import { GroupModeActions } from "@app/scene/map/store/groupMode/actions";
import { MapModeActions } from "@app/scene/map/store/mapMode/actions";
import MapToolPayload from "@app/scene/map/store/MapToolPayload";
import { PinLocationActions } from "@app/scene/map/store/pinLocation/actions";
import { TerritoryModeActions } from "@app/scene/map/store/territoryMode/actions";
import { ToolbarActions } from "@app/scene/map/store/toolbar/actions";
import PinDropModel from "@app/scene/map/types/PinDropModel";
import GeoPath from "@app/types/GeoPath";
import { MapFetcherPayload } from "@app/types/map/MapFetcher";
import MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import MapViewportState from "@app/types/map/MapViewportState";
import RulerPath from "@app/types/map/RulerPath";

export const initializeMapView = createAsyncAction(
  "map/initialize/request",
  "map/initialize/success",
  "map/initialize/failure"
)<(viewport: MapPersistedViewportState) => void, void, unknown>();

export const updateViewportState = createAction("map/updateViewportState")<MapViewportState>();

export const selectMapTool = createAsyncAction(
  "map/selectTool/request",
  "map/selectTool/success",
  "map/selectTool/cancel"
)<MapToolPayload, MapToolPayload, void>();

export const updatePinDropCoordinates = createAsyncAction(
  "map/pinDrop/updateCoordinates/request",
  "map/pinDrop/updateCoordinates/success",
  "map/pinDrop/updateCoordinates/failure"
)<GeolocationCoordinates | undefined, Located | undefined, unknown>();

export const appendLassoPath = createAction("map/lasso/appendPath")<GeoPath>();
export const cleanLassoPaths = createAction("map/lasso/cleanPaths")<void>();

export const updateLassoClusters = createAction("map/lasso/updateLassoClusters")<
  Cluster[] | undefined
>();
export const setLassoContainsFlags = createAction("map/lasso/setLassoContainsFlags")<{
  containsClusters?: boolean;
}>();

export const enterLassoMode = createAction("map/lasso/enterLassoMode")<void>();
export const exitLassoMode = createAction("map/lasso/exitLassoMode")<void>();
export const refreshLassoMode = createAction("map/lasso/refreshLassoMode")<void>();

export const toggleRulerPin = createAction("map/ruler/togglePin")<EntityPin | Lead | MultiPin>();
export const updateRulerPath = createAction("map/ruler/setPath")<RulerPath>();
export const exitRulerMode = createAction("map/ruler/exit")<void>();

export const enterMode = createAction("map/enterMode")<MapMode | undefined>();
export const exitMode = createAction("map/exitMode")<void>();

export const fetchLassoSelection = createAsyncAction(
  "map/lasso/fetchLassoSelection/request",
  "map/lasso/fetchLassoSelection/success",
  "map/lasso/fetchLassoSelection/failure"
)<MapFetcherPayload, void, unknown>();

export const setMapSettings = createAction("map/setMapSettings")<
  Partial<PersistentMapSettings> | undefined
>();

export const setLocationSearchVisibility = createAction("map/toggleLocationSearch")<boolean>();

export const setPinDropPlace = createAction("map/pinDrop/setPlace")<PinDropModel["place"]>();

export * from "./groupMode/actions";
export * from "./mapMode/actions";
export * from "./pinLocation/actions";
export * from "./territoryMode/actions";
export * from "./toolbar/actions";

export type Actions =
  | ActionType<
      | typeof appendLassoPath
      | typeof cleanLassoPaths
      | typeof enterLassoMode
      | typeof enterMode
      | typeof exitLassoMode
      | typeof exitMode
      | typeof exitRulerMode
      | typeof fetchLassoSelection
      | typeof initializeMapView
      | typeof refreshLassoMode
      | typeof selectMapTool
      | typeof setLassoContainsFlags
      | typeof setLocationSearchVisibility
      | typeof setMapSettings
      | typeof setPinDropPlace
      | typeof toggleRulerPin
      | typeof updateLassoClusters
      | typeof updatePinDropCoordinates
      | typeof updateRulerPath
      | typeof updateViewportState
    >
  | GroupModeActions
  | MapModeActions
  | PinLocationActions
  | TerritoryModeActions
  | ToolbarActions;
